import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  message,
  Row,
  Space,
  Switch,
  Table,
  Tag,
  Tooltip,
  Modal,
} from "antd";
import {
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import { coreApi } from "../api/calls";
import CampaignAddForm from "../components/Campaigns/CampaignAddForm";
import CampaignEditForm from "../components/Campaigns/CampaignEditForm";

const CampaignsIndex = (props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [campaignsList, setCampaignsList] = useState([]);
  const [pagination, setPagination] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);

  const [filters, setFilters] = useState({
    pageSize: 10,
    page: 1,
  });

  useEffect(() => {
    loadCampaigns();
  }, [filters]);

  const loadCampaigns = () => {
    setIsLoading(true);
    coreApi
      .get("/campaigns", { params: filters })
      .then((response) => {
        // Backend может вернуть либо массив, либо объект с пагинацией
        if (Array.isArray(response.data)) {
          setCampaignsList(response.data);
          setPagination(null);
        } else {
          const { data, ...paginationData } = response.data;
          setCampaignsList(data);
          setPagination(paginationData);
        }
      })
      .catch((error) => {
        console.error(error);
        message.error(t("campaigns.loadError"));
      })
      .finally(() => setIsLoading(false));
  };

  const handleToggleStatus = (campaignId, currentStatus) => {
    coreApi
      .patch(`/campaigns/${campaignId}/toggle`)
      .then((response) => {
        message.success(t("campaigns.statusUpdated"));
        loadCampaigns();
      })
      .catch((error) => {
        console.error(error);
        message.error(t("campaigns.statusUpdateError"));
      });
  };

  const handleDelete = (campaignId, campaignName) => {
    Modal.confirm({
      title: t("campaigns.deleteConfirmTitle"),
      content: t("campaigns.deleteConfirmContent", { name: campaignName }),
      okText: t("common.confirmAnswer"),
      okType: "danger",
      cancelText: t("common.rejectAnswer"),
      onOk: () => {
        coreApi
          .delete(`/campaigns/${campaignId}`)
          .then((response) => {
            message.success(t("campaigns.deleteSuccess"));
            loadCampaigns();
          })
          .catch((error) => {
            console.error(error);
            message.error(t("campaigns.deleteError"));
          });
      },
    });
  };

  const columns = [
    {
      title: t("campaigns.name"),
      dataIndex: "name",
      key: "name",
      sorter: true,
      render: (name, record) => (
        <Link to={`/campaigns/${record.id}/banners`} style={{ fontWeight: 500 }}>
          {name}
        </Link>
      ),
    },
    {
      title: t("campaigns.description"),
      dataIndex: "description",
      key: "description",
      ellipsis: true,
      render: (description) => (
        <Tooltip title={description}>
          <span>{description || "—"}</span>
        </Tooltip>
      ),
    },
    {
      title: t("campaigns.citiesCount"),
      key: "cities_count",
      width: 120,
      align: "center",
      render: (record) => (
        <Tag color="blue">{record.city_branches?.length || 0}</Tag>
      ),
    },
    {
      title: t("campaigns.bannersCount"),
      key: "banners_count",
      width: 120,
      align: "center",
      render: (record) => (
        <Tag color="green">{record.banners?.length || 0}</Tag>
      ),
    },
    {
      title: t("campaigns.status"),
      dataIndex: "is_active",
      key: "is_active",
      width: 120,
      align: "center",
      render: (isActive, record) => (
        <Switch
          checked={isActive}
          onChange={() => handleToggleStatus(record.id, isActive)}
        />
      ),
    },
    {
      title: t("campaigns.type"),
      dataIndex: "is_recurring",
      key: "is_recurring",
      width: 120,
      align: "center",
      render: (isRecurring) => (
        <Tag color={isRecurring ? "purple" : "orange"}>
          {isRecurring ? t("campaigns.recurring") : t("campaigns.oneTime")}
        </Tag>
      ),
    },
    {
      title: t("common.actions"),
      key: "actions",
      width: 150,
      align: "center",
      render: (record) => (
        <Space>
          <Tooltip title={t("campaigns.manageBanners")}>
            <Button
              icon={<EyeOutlined />}
              onClick={() => history.push(`/campaigns/${record.id}/banners`)}
            />
          </Tooltip>
          <Tooltip title={t("common.edit")}>
            <Button
              icon={<EditOutlined />}
              onClick={() => setEditingCampaign(record)}
            />
          </Tooltip>
          <Tooltip title={t("common.delete")}>
            <Button
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record.id, record.name)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  const handleTableChange = (paginating, filter, sorting) => {
    const newFilters = {
      ...filters,
      pageSize: paginating.pageSize,
      page: paginating.current,
      sorterOrder: sorting.order,
      sorterKey: sorting.columnKey,
    };
    setFilters(newFilters);
  };

  return (
    <Row gutter={[16, 24]}>
      <Col span={24}>
        <Card>
          <Table
            size="small"
            loading={isLoading}
            rowKey="id"
            dataSource={campaignsList}
            columns={columns}
            onChange={handleTableChange}
            pagination={{
              current: pagination?.current_page || 1,
              pageSize: pagination?.per_page || filters.pageSize,
              total: pagination?.total || campaignsList.length,
              showSizeChanger: true,
              showTotal: (total, range) => (
                <p>
                  {t("common.paginationPartOne")} <strong>{range[0]}</strong>-<strong>{range[1]}</strong>
                  {" " + t("common.paginationPartTwo") + " "}
                  <strong>{total}</strong> {t("common.paginationPartThree")}
                </p>
              ),
            }}
            locale={{
              emptyText: (
                <div style={{ padding: "40px 0" }}>
                  <p>{t("campaigns.emptyState")}</p>
                </div>
              ),
            }}
          />
        </Card>
      </Col>

      <Col span={24}>
        <CampaignAddForm
          onSuccess={() => loadCampaigns()}
        />
      </Col>

      {editingCampaign && (
        <CampaignEditForm
          campaign={editingCampaign}
          onSuccess={() => {
            loadCampaigns();
            setEditingCampaign(null);
          }}
          onCancel={() => setEditingCampaign(null)}
        />
      )}
    </Row>
  );
};

export default CampaignsIndex;
